import { Button, IconButton } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { HiSearch } from "react-icons/hi";
import { SchoolSettingsEditor } from "../../components/analytics/settings/SchoolSettingsEditor";
import { AccessibleSchoolsDocument } from "../../generated/graphql";

export function SchoolSettingsPage() {
  const { data, loading: isLoading, error, refetch } = useQuery(AccessibleSchoolsDocument);
  const [expandedSchoolId, setExpandedSchoolId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">Loading schools...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-error-500">Error loading schools: {error.message}</div>
        </div>
      </div>
    );
  }

  if (!data?.accessibleSchools || data.accessibleSchools.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="h3 mb-2">School FSRS Settings</h1>
        <p className="caption mb-8">Manage FSRS settings for individual schools.</p>
        <div className="panel p-12 text-center">
          <p className="text-gray-600">No accessible schools found</p>
        </div>
      </div>
    );
  }

  const schools = data.accessibleSchools;

  // Filter schools based on search query
  const filteredSchools = schools.filter((school) =>
    school.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-2">
        <h1 className="h3">School FSRS Settings</h1>

        {/* Search Bar */}
        <div className="relative w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <HiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search schools..."
            className="block w-full pl-10 pr-20 py-2 bg-surface border border-gray-800 rounded-md leading-5 text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
          />
          {searchQuery && (
            <Button
              type="button"
              onClick={() => setSearchQuery("")}
              variant="ghost"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <p className="caption mb-8">
        Manage FSRS settings for individual schools. Click "Edit Settings" to configure each school.
      </p>

      <div className="space-y-4">
        {filteredSchools.map((school) => {
          const isExpanded = expandedSchoolId === school.id;

          return (
            <div key={school.id} className="panel overflow-hidden">
              {/* School Header */}
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="h5">{school.name}</h3>
                    <p className="mt-1 caption">
                      FSRS Status:{" "}
                      {school.fsrsEnabled ? (
                        <span className="text-success-500 font-medium">Enabled</span>
                      ) : (
                        <span className="text-gray-600 font-medium">Disabled</span>
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setExpandedSchoolId(isExpanded ? null : school.id)}
                    variant="secondary"
                    className="text-white bg-primary-50 border-primary-200 hover:bg-primary-100"
                  >
                    {isExpanded ? "Hide Settings" : "Edit Settings"}
                  </Button>
                </div>
              </div>

              {/* Settings Editor (Collapsible) */}
              {isExpanded && (
                <div className="p-6 bg-surface">
                  <SchoolSettingsEditor
                    schoolId={school.id}
                    schoolName={school.name}
                    currentSettings={school.fsrsSettings}
                    onUpdate={() => refetch()}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
